/**
 * Active workspace context (US-004).
 *
 * Mirrors ActiveRuntimeContext exactly. Loads /api/workspaces on mount,
 * picks the slug last persisted in localStorage (or "default" on first
 * use), exposes a switcher, and pushes the slug into the api.* layer's
 * interceptor so every outgoing request gets `?workspace=<slug>`.
 *
 * Switching workspace clears the active-runtime selection — each
 * workspace owns its own runtime registry and the previous selection
 * isn't valid in the new context.
 *
 * What is remembered (localStorage + the cookie) is only a preference. Each
 * time the server's list of the caller's workspaces arrives, `refresh()`
 * checks it against that list (`resolveSlug`): a workspace that was deleted,
 * or that the caller lost its membership in, is replaced by one the caller
 * still has — or cleared when it has none — so the backend, which never falls
 * back for a workspace asked for by name, is not asked for a dead one again.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, setActiveWorkspaceSlug } from '../api';
import type { WorkspaceSummary } from '../types';

const STORAGE_KEY = 'pipebase:active-workspace';
const RUNTIME_STORAGE_KEY = 'pipebase:active-runtime';
/** Cookie the backend reads when neither `?workspace=` nor the
 *  X-Pipebase-Workspace header is set (selectWorkspace, workspace-context.ts).
 *  An empty `?workspace=` counts as unset, so it is cleared or overwritten
 *  here, never reset from the query. 1 year. */
const COOKIE_NAME = 'pb_active_workspace';

interface ActiveWorkspaceState {
  workspaces: WorkspaceSummary[];
  /** Selected workspace, or null while loading. */
  active: WorkspaceSummary | null;
  loading: boolean;
  /** Pick a workspace by slug. Clears the runtime selection (different
   *  workspaces have different runtimes). */
  setActive: (slug: string) => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<ActiveWorkspaceState>({
  workspaces: [],
  active: null,
  loading: true,
  setActive: () => undefined,
  refresh: () => Promise.resolve(),
});

function readStoredSlug(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEY);
}

function persistSlug(slug: string | null): void {
  if (typeof window === 'undefined') return;
  if (slug) {
    localStorage.setItem(STORAGE_KEY, slug);
    // Mirror to a plain cookie so the backend's selectWorkspace cookie
    // path can pick it up — the JWT session cookie is HttpOnly and we
    // can't write the active-workspace claim from JS, but a plain
    // cookie works just as well as a fallback hint.
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(slug)}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
  } else {
    localStorage.removeItem(STORAGE_KEY);
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
  }
}

/**
 * The workspace to act in, given the workspaces the server lists for this
 * caller and the slug remembered from last time: the remembered one while it
 * is still listed, else `default`, else the first listed, else none.
 */
function resolveSlug(list: WorkspaceSummary[], remembered: string | null): string | null {
  if (remembered && list.some((w) => w.slug === remembered)) return remembered;
  return (list.find((w) => w.slug === 'default') ?? list[0])?.slug ?? null;
}

export function ActiveWorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [activeSlug, setActiveSlug] = useState<string | null>(() => readStoredSlug());
  const [loading, setLoading] = useState(true);
  // What refresh() compares the list against; it cannot read `activeSlug`
  // without being recreated on every change.
  const slugRef = useRef<string | null>(activeSlug);

  // Keep the api interceptor in sync with our chosen slug — even before
  // the list comes back, so the very first list call sees its own slug.
  useEffect(() => {
    setActiveWorkspaceSlug(activeSlug);
  }, [activeSlug]);

  // Remember `slug` (or forget, for null) everywhere at once: state, storage,
  // the cookie and the interceptor.
  const select = useCallback((slug: string | null) => {
    slugRef.current = slug;
    setActiveSlug(slug);
    persistSlug(slug);
    setActiveWorkspaceSlug(slug);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { workspaces: list } = await api.listWorkspaces();
      // Decided here, not in an effect: a provider's effects run after its
      // children's, so a request a child fires as the list arrives would
      // still name the old workspace. Only on a list that came back — a
      // failed request says nothing about which workspaces exist, and must
      // not wipe what the user picked.
      select(resolveSlug(list, slugRef.current));
      setWorkspaces(list);
    } catch {
      /* keep the previous list, and the remembered workspace, on failure */
    } finally {
      setLoading(false);
    }
  }, [select]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const resolved = resolveSlug(workspaces, activeSlug);
  const active: WorkspaceSummary | null = workspaces.find((w) => w.slug === resolved) ?? null;

  const setActive = useCallback(
    (slug: string) => {
      select(slug);
      // Clear the runtime selection — each workspace has its own.
      if (typeof window !== 'undefined') {
        localStorage.removeItem(RUNTIME_STORAGE_KEY);
      }
    },
    [select],
  );

  return (
    <Ctx.Provider value={{ workspaces, active, loading, setActive, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useActiveWorkspace(): ActiveWorkspaceState {
  return useContext(Ctx);
}
